import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import { SideMenu } from './side-menu';
import { useObservable } from 'rxjs-hooks';
import { DataFeedInput } from '@server/data-feed/schemas/input.schema';
import type { WorkspaceLayout } from '@server/trading-layouts/schemas/output.schema';
import { useLiveTradingApi } from './live-trading.hook';
import { useChartState } from '@client/state/chart-state.hook';
import { SymbolLayout } from '@client/widgets/layouts/symbol-layout';
import { OpenTradingFormButton } from '@client/widgets/trading-form/open-trading-form-button';
import { TradingWorkspaceProvider } from '@client/features/trading-workspace/trading-workspace.provider';
import { getWorkspaceFullscreen$ } from '@client/widgets/layouts/workspace-fullscreen.service';
import { WorkspaceFullscreenToggle } from '@client/widgets/layouts/workspace-fullscreen-toggle';
import { convertDataFeedConfigToFormState } from './live-trading.utils';
import { useWidget } from '@client/widgets/widget.hook';
import { trpc } from '@client-trpc';
import { AppIconButton } from '@client/foundation/design-system/components';
import { resolveLayoutStatesToFormState } from '@client/features/trading-layouts/layout-template-state.utils';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';

const LiveTradingContent: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(true);
  const [activeWorkspaceLayoutId, setActiveWorkspaceLayoutId] = useState<
    string | undefined
  >(undefined);
  const menuOpenBeforeFullscreenRef = useRef<boolean | null>(null);
  const { getState$, dispatch } = useChartState();
  const { openWidget } = useWidget();
  const { feedInputConfigs, closeFeed, clearPersistedTrades } =
    useLiveTradingApi();
  const { notify } = useNotifications();
  const trpcClient = trpc.useUtils().client;
  const defaultWorkspaceAttemptedRef = useRef(false);

  const chartState = useObservable(() => getState$(), new Map());
  const workspaceFullscreen = useObservable(() => getWorkspaceFullscreen$());

  const applyWorkspaceLayout = useCallback(
    async (layout: WorkspaceLayout) => {
      const resolvedFormStates = await resolveLayoutStatesToFormState(
        trpcClient,
        layout.snapshot.charts.map(({ layout: chartLayout }) => chartLayout.formState)
      );

      dispatch({ type: 'CLEAR_STATE' });
      layout.snapshot.charts.forEach(({ chartId, layout: chartLayout }, index) => {
        dispatch({
          type: 'ADD_CHART',
          payload: {
            id: chartId,
            formState: resolvedFormStates[index]
          }
        });
      });
      setActiveWorkspaceLayoutId(layout.id);
    },
    [dispatch, trpcClient]
  );

  const tryApplyDefaultWorkspaceLayout = useCallback(async () => {
    try {
      const defaultWorkspace =
        await trpcClient.workspaceLayouts.defaultWorkspaceLayout.query();
      if (!defaultWorkspace) {
        return false;
      }

      await applyWorkspaceLayout(defaultWorkspace);
      return true;
    } catch (error) {
      console.warn('Failed to load default workspace layout:', error);
      return false;
    }
  }, [applyWorkspaceLayout, trpcClient]);

  useEffect(() => {
    if (defaultWorkspaceAttemptedRef.current) {
      return;
    }
    if (chartState.size > 0) {
      return;
    }

    defaultWorkspaceAttemptedRef.current = true;
    void tryApplyDefaultWorkspaceLayout();
  }, [chartState.size, tryApplyDefaultWorkspaceLayout]);

  useEffect(() => {
    if (workspaceFullscreen) {
      if (menuOpenBeforeFullscreenRef.current === null) {
        menuOpenBeforeFullscreenRef.current = menuOpen;
      }
      if (menuOpen) {
        setMenuOpen(false);
      }
      return;
    }

    if (menuOpenBeforeFullscreenRef.current !== null) {
      setMenuOpen(menuOpenBeforeFullscreenRef.current);
      menuOpenBeforeFullscreenRef.current = null;
    }
  }, [workspaceFullscreen, menuOpen]);

  // Toggle the side menu
  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  // Handle loading a chart
  const handleLoadChart = useCallback(
    (config: DataFeedInput) => {
      const formState = convertDataFeedConfigToFormState(config);
      setActiveWorkspaceLayoutId(undefined);
      dispatch({
        type: 'ADD_CHART',
        payload: { formState, id: config.feedId }
      });
    },
    [dispatch]
  );

  const handleClose = useCallback(
    (config: DataFeedInput) => {
      setActiveWorkspaceLayoutId(undefined);
      dispatch({ type: 'DELETE_CHART', payload: { id: config.feedId } });
      closeFeed({
        feedId: config.feedId
      });
    },
    [closeFeed, dispatch]
  );

  const handleDuplicate = useCallback(
    (config: DataFeedInput) => {
      openWidget({
        type: 'TradingForm',
        mode: config.marketConfig.tradingMode,
        formState: convertDataFeedConfigToFormState(config),
        initSource: 'DUPLICATE_FORM'
      });
    },
    [openWidget]
  );

  const handleClearSavedTrades = useCallback(
    async (config: DataFeedInput) => {
      try {
        const result = await clearPersistedTrades({ feedId: config.feedId });
        notify({
          message: `Cleared ${result.deletedCount} saved trade${
            result.deletedCount === 1 ? '' : 's'
          }.`,
          type: 'success'
        });
      } catch (error) {
        notify({
          message: 'Failed to clear saved trades.',
          type: 'error'
        });
      }
    },
    [clearPersistedTrades, notify]
  );

  const handleSaveWorkspaceLayout = useCallback(() => {
    openWidget({ type: 'SaveWorkspaceLayoutForm' });
  }, [openWidget]);

  const handleManageWorkspaceLayouts = useCallback(() => {
    openWidget({
      type: 'ManageWorkspaceLayouts',
      activeLayoutId: activeWorkspaceLayoutId,
      onApplied: (layoutId) => setActiveWorkspaceLayoutId(layoutId)
    });
  }, [activeWorkspaceLayoutId, openWidget]);

  const handleOpenTradingForm = useCallback(() => {
    openWidget({ type: 'TradingForm', mode: 'LIVE', initSource: 'NEW_FORM' });
  }, [openWidget]);

  // Determine if charts are available
  const hasCharts = chartState.size > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <WorkspaceFullscreenToggle />
      <Box sx={{ display: 'flex', flex: 1 }}>
        {/* Side Menu */}
        <SideMenu
          open={menuOpen}
          feedInputConfigs={feedInputConfigs || ([] as DataFeedInput[])}
          onToggleMenu={toggleMenu}
          onLoadChart={handleLoadChart}
          onClose={handleClose}
          onDuplicate={handleDuplicate}
          onClearSavedTrades={handleClearSavedTrades}
        />

        {/* Main Content */}
        <Box
          flex={1}
          mt={workspaceFullscreen ? 0 : 1}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          {!workspaceFullscreen && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1.7 }}
            >
              <OpenTradingFormButton
                mode="LIVE"
                sx={{ pl: 0 }}
                onOpen={handleOpenTradingForm}
              />
              <AppIconButton
                title="Save workspace"
                tone="primary"
                onClick={handleSaveWorkspaceLayout}
              >
                <SaveOutlinedIcon />
              </AppIconButton>
              <AppIconButton
                title="Manage workspaces"
                tone="primary"
                onClick={handleManageWorkspaceLayouts}
              >
                <DashboardCustomizeOutlinedIcon />
              </AppIconButton>
            </Box>
          )}
          {hasCharts ? (
            <SymbolLayout />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary'
              }}
            >
              No live chart open
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const LiveTrading: React.FC = () => {
  return (
    <TradingWorkspaceProvider>
      <LiveTradingContent />
    </TradingWorkspaceProvider>
  );
};
